import React, { useEffect } from "react";
import { ThemeProvider } from "@mui/material";
import { useLocation } from "react-router-dom";
import {
    TintoaThemeProvider,
    useTintoaColorMode,
    createTintoaTheme
} from "@tintoa/ui";
// Activates the central MUI palette augmentation (overlay / authCard / input / semantic).
import "@tintoa/ui/theme-augmentation";
import { fetchWithError } from "../utils/fetchWithError.ts";
import { getEffectiveUiLanguage } from "../config/supportedLanguages";

/**
 * App theme hook. Backed by `@tintoa/ui`'s `TintoaThemeProvider`, which loads
 * the persisted theme from `/session/settings` and persists changes via
 * `PUT /session/settings/global`. Returns `{ mode, toggleColorMode, setColorMode, refresh }`.
 */
export const useTheme = useTintoaColorMode;

/** Keeps `<html lang>` in sync with the user's stored language setting. */
const LanguageSync: React.FC = () => {
    const location = useLocation();
    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const settings = await fetchWithError<{
                    global?: { language?: string };
                }>("/session/settings", { credentials: "include" });
                if (cancelled) return;
                document.documentElement.lang = getEffectiveUiLanguage(
                    settings?.global?.language
                );
            } catch {
                /* not signed in or request failed — leave current lang */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [location.pathname]);
    return null;
};

export const AppThemeProvider: React.FC<{ children: React.ReactNode }> = ({
    children
}) => (
    // `cssBaseline={false}`: the app renders its own `<CssBaseline />` (see App.tsx).
    // Backend persistence is enabled via the `TintoaConfigProvider` in App.tsx.
    <TintoaThemeProvider cssBaseline={false}>
        <LanguageSync />
        {children}
    </TintoaThemeProvider>
);

/** Wraps auth pages (login/signup) so they always use the dark theme. */
export const AuthPagesThemeProvider: React.FC<{ children: React.ReactNode }> = ({
    children
}) => <ThemeProvider theme={createTintoaTheme("dark")}>{children}</ThemeProvider>;
